import { Archive } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { DashboardTable } from './DashboardTable';
import { DashboardKarten } from './DashboardKarten';
import { anzahl } from '@/lib/format';
import type { BestellungZeile } from '@/types/dashboard';

type Props = {
  zeilen: BestellungZeile[];
  total: number;
  suchbegriff: string;
  istSchmal: boolean;
  visibleColumns: string[];
};

/**
 * Archiv-Treffer unter den aktiven — als abgesetzte Gruppe (E12, A10.5).
 *
 * WARUM DAS NICHT DASSELBE IST WIE „Archivierte einbeziehen"
 *   Der alte Schalter mischte Erledigtes unter die offenen Vorgänge, und
 *   man musste ihn erst finden. Wer ihn nicht kannte, las „Keine
 *   Bestellungen gefunden" und schloss daraus „gibt es nicht" — an der
 *   Kundin am Tresen vorbei, die gerade danach fragt.
 *
 *   Deshalb liefert die Suche Archiv-Treffer jetzt IMMER mit, aber
 *   getrennt: eigene Überschrift, eigene Zahl, sichtbare Trennlinie. Wer
 *   die Liste überfliegt, sieht sofort, dass diese Bestellungen erledigt
 *   sind, und verwechselt sie nicht mit Arbeit.
 *
 *   Ohne Suchbegriff erscheint hier nichts. Das Dashboard zeigt den
 *   Arbeitsvorrat; Erledigtes gehört nur dann dazu, wenn jemand
 *   ausdrücklich danach fragt.
 */
export function ArchivTrefferGruppe({
  zeilen,
  total,
  suchbegriff,
  istSchmal,
  visibleColumns
}: Props) {
  if (zeilen.length === 0) return null;

  return (
    <section
      data-tour="archiv-treffer"
      className="border-t-4 border-line bg-cream-warm/40"
      aria-label="Treffer im Archiv"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2">
        <h2 className="flex items-center gap-2 text-sm font-medium text-ink-soft">
          <Archive className="h-4 w-4" aria-hidden />
          Im Archiv: {anzahl(total, 'Treffer', 'Treffer')}
        </h2>
        <p className="text-xs text-ink-faint">
          Diese Bestellungen sind erledigt.{' '}
          <Link
            to="/archiv"
            search={{ q: suchbegriff }}
            className="underline underline-offset-2 hover:text-akzent"
          >
            Im Archiv weitersuchen
          </Link>
        </p>
      </div>

      {/* Dieselbe Tabelle wie oben: dieselben Spalten, dieselbe
          Verlinkung in die Bestellung. Eine zweite Darstellung wäre eine
          zweite Wahrheit über dasselbe Ding. */}
      {istSchmal ? (
        <DashboardKarten zeilen={zeilen} />
      ) : (
        <DashboardTable
          zeilen={zeilen}
          visibleColumns={visibleColumns}
          sort="dringlichkeit"
          onSortChange={() => undefined}
        />
      )}

      {total > zeilen.length && (
        <p className="px-4 py-2 text-xs text-ink-faint">
          {total - zeilen.length} weitere im Archiv —{' '}
          <Link
            to="/archiv"
            search={{ q: suchbegriff }}
            className="underline underline-offset-2 hover:text-akzent"
          >
            dort ansehen
          </Link>
        </p>
      )}
    </section>
  );
}
